/**
 * Frakt — the freight booking queue and booking screen (P25009-450).
 *
 * Two fetches, one page:
 *   - the QUEUE: this seller's `purchased` sales whose delivery method is
 *     shipping (BR-6 — pickup orders never enter it). Delivery method is not a
 *     queryable transaction field, so the purchased page is pulled and
 *     filtered here; a seller's live to-do list fits one page of 100.
 *   - one BOOKING transaction by id, for /frakt/:id — fetched on its own so a
 *     deep link works without the queue, and so an already-delivered order
 *     (re-printing a slip, correcting a tracking number) still resolves.
 *
 * Booking (submitFreightBooking) is the BR-5 sequence, in this exact order:
 *   1. /api/freight-booking stamps metadata.freight — tracking number, weight,
 *      package, carrier cost — after verifying the caller is the provider.
 *   2. the ordinary provider mark-delivered fires (the same transition the
 *      order row's "Markera som skickad" uses), so the transition event's
 *      snapshot already carries the stamp and the buyer's order-shipped mail
 *      can print the tracking number.
 *   3. capture-payment, exactly like the row handler — the cron sweep is the
 *      backstop, so a capture failure never fails the booking.
 */

import { storableError } from '../../util/errors';
import { types as sdkTypes } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchFreightQueueCount } from '../../ducks/freightQueue.duck';
import { stampFreightBooking, capturePayment } from '../../util/api';
import { transitions as purchaseTransitions } from '../../transactions/transactionProcessPurchase';

// ================ Action types ================ //

export const FETCH_QUEUE_REQUEST = 'app/FreightPage/FETCH_QUEUE_REQUEST';
export const FETCH_QUEUE_SUCCESS = 'app/FreightPage/FETCH_QUEUE_SUCCESS';
export const FETCH_QUEUE_ERROR = 'app/FreightPage/FETCH_QUEUE_ERROR';

export const FETCH_TX_REQUEST = 'app/FreightPage/FETCH_TX_REQUEST';
export const FETCH_TX_SUCCESS = 'app/FreightPage/FETCH_TX_SUCCESS';
export const FETCH_TX_ERROR = 'app/FreightPage/FETCH_TX_ERROR';

export const BOOKING_REQUEST = 'app/FreightPage/BOOKING_REQUEST';
export const BOOKING_SUCCESS = 'app/FreightPage/BOOKING_SUCCESS';
export const BOOKING_ERROR = 'app/FreightPage/BOOKING_ERROR';

// ================ Reducer ================ //

const initialState = {
  queueRefs: [],
  fetchQueueInProgress: false,
  fetchQueueError: null,
  bookingTxRef: null,
  fetchTxInProgress: false,
  fetchTxError: null,
  bookingInProgress: false,
  bookingError: null,
  /* The tx id of the booking that just succeeded, so the screen can swap to
   * its confirmation state without a refetch race. */
  bookedTxId: null,
};

export default function freightPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_QUEUE_REQUEST:
      return { ...state, fetchQueueInProgress: true, fetchQueueError: null };
    case FETCH_QUEUE_SUCCESS:
      return { ...state, fetchQueueInProgress: false, queueRefs: payload };
    case FETCH_QUEUE_ERROR:
      return { ...state, fetchQueueInProgress: false, fetchQueueError: payload };

    case FETCH_TX_REQUEST:
      /* The ref is kept during a refetch — the post-booking refresh must not
       * blank the confirmation screen while the same order reloads. */
      return { ...state, fetchTxInProgress: true, fetchTxError: null };
    case FETCH_TX_SUCCESS:
      return { ...state, fetchTxInProgress: false, bookingTxRef: payload };
    case FETCH_TX_ERROR:
      return { ...state, fetchTxInProgress: false, fetchTxError: payload };

    case BOOKING_REQUEST:
      return { ...state, bookingInProgress: true, bookingError: null };
    case BOOKING_SUCCESS:
      return { ...state, bookingInProgress: false, bookedTxId: payload };
    case BOOKING_ERROR:
      return { ...state, bookingInProgress: false, bookingError: payload };

    default:
      return state;
  }
}

// ================ Thunks ================ //

/* The same field set the order lists fetch, plus the listing fields the
 * freight preset derives from (adType + sales unit → package + weight). */
const TX_QUERY_PARAMS = {
  include: ['listing', 'customer', 'provider'],
  'fields.transaction': [
    'processName',
    'lastTransition',
    'lastTransitionedAt',
    'transitions',
    'payinTotal',
    'lineItems',
    'protectedData',
    'metadata',
  ],
  'fields.listing': [
    'title',
    'publicData.adType',
    'publicData.listingType',
    'publicData.salesUnit',
    'publicData.setSize',
    'publicData.location',
    'publicData.warehouse',
  ],
  'fields.user': ['profile.displayName'],
};

const refOf = entity => ({ id: entity.id, type: entity.type });

export const fetchFreightQueue = () => (dispatch, getState, sdk) => {
  dispatch({ type: FETCH_QUEUE_REQUEST });

  return sdk.transactions
    .query({
      only: 'sale',
      // The state/ prefix is load-bearing — a bare 'purchased' silently
      // matches nothing (see the order-list filter, whose chip values are
      // `state/<name>` for exactly this reason).
      states: 'state/purchased',
      perPage: 100,
      ...TX_QUERY_PARAMS,
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      /* BR-6: only shipping orders enter the queue. */
      const refs = (response.data.data || [])
        .filter(tx => tx.attributes?.protectedData?.deliveryMethod === 'shipping')
        .map(refOf);
      dispatch({ type: FETCH_QUEUE_SUCCESS, payload: refs });
      return response;
    })
    .catch(e => {
      dispatch({ type: FETCH_QUEUE_ERROR, payload: storableError(e) });
      throw e;
    });
};

export const fetchBookingTx = txId => (dispatch, getState, sdk) => {
  dispatch({ type: FETCH_TX_REQUEST });

  const id = typeof txId === 'string' ? new sdkTypes.UUID(txId) : txId;
  return sdk.transactions
    .show({ id, ...TX_QUERY_PARAMS })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch({ type: FETCH_TX_SUCCESS, payload: refOf(response.data.data) });
      return response;
    })
    .catch(e => {
      dispatch({ type: FETCH_TX_ERROR, payload: storableError(e) });
      throw e;
    });
};

/**
 * @param {Object} tx      the denormalised booking transaction
 * @param {Object} values  { trackingNumber, carrier, weightKg, colliType,
 *                           carrierCostSubunits }
 */
export const submitFreightBooking = (tx, values) => (dispatch, getState, sdk) => {
  dispatch({ type: BOOKING_REQUEST });

  const txId = tx.id.uuid;

  return stampFreightBooking({ transactionId: txId, ...values })
    .then(res => {
      if (res && res.alreadyDelivered) return null;
      return sdk.transactions.transition({
        id: tx.id,
        transition: purchaseTransitions.MARK_DELIVERED,
        params: {},
      });
    })
    .then(async () => {
      /* Same follow-up as the order row's mark-delivered: capture now rather
       * than leaving the money to the ten-minute cron. A failure here is the
       * cron's problem, not the booking's. */
      // Hosted-checkout orders only, as before; the server reads the payment
      // from the order itself, never from this call.
      const { stripeSessionId } = tx.attributes?.metadata || {};
      if (stripeSessionId) {
        try {
          await capturePayment({ transactionId: txId });
        } catch (e) {
          console.error('freight booking: capture-payment failed, cron sweep will retry', e);
        }
      }
    })
    .then(() => {
      dispatch({ type: BOOKING_SUCCESS, payload: txId });
      /* The order left `purchased`: refresh the record, the queue and the nav
       * badge so nothing still claims it is waiting. */
      dispatch(fetchBookingTx(tx.id));
      dispatch(fetchFreightQueue());
      dispatch(fetchFreightQueueCount({ force: true }));
      return { booked: true };
    })
    .catch(e => {
      dispatch({ type: BOOKING_ERROR, payload: storableError(e) });
      throw e;
    });
};

// ================ loadData ================ //

export const loadData = params => dispatch => {
  const fetches = [dispatch(fetchFreightQueue())];
  if (params?.id) {
    fetches.push(dispatch(fetchBookingTx(params.id)));
  }
  return Promise.all(fetches.map(p => p.catch(() => null)));
};
